/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react'
import {
  Center,
  Td,
  Th,
  Tr,
  Text,
  Tbody,
  Thead,
  Tfoot,
  Table,
  chakra,
  useColorModeValue,
  TableContainer,
  Box,
  Button,
  Progress,
} from '@chakra-ui/react'
import {
  ColumnDef,
  useReactTable,
  SortingState,
  getSortedRowModel,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import { useTranslations } from 'next-intl'
import { ITransactionFilters } from '@/types/billing.interface'

type IProps<Data extends object> = {
  data: Data[]
  columns: ColumnDef<Data, any>[]
  loading?: boolean
  withSortableColumns?: boolean
  className?: string
  resetTableListeningState?: boolean
  variant?: string
  transparent?: boolean
  isFilterOn?: boolean
  applyFilters?: (filters: ITransactionFilters | null) => void
  borderRadius?: string
  bg?: string
  dividerColor?: string
  borderWidth?: number
  borderColor?: string
  removeLastLine?: boolean
  noDataText?: string
  headerBorderBottom?: string
}

function getDataColumnValue(cell: any): string | number | undefined {
  const metaTitle = cell?.column?.columnDef?.meta?.title

  if (typeof metaTitle === 'string' || typeof metaTitle === 'number') {
    return metaTitle
  }

  const title = cell?.column?.columnDef?.title
  const header = cell?.column?.columnDef?.header

  if (typeof title === 'string' || typeof title === 'number') {
    return title
  }

  if (typeof header === 'string' || typeof header === 'number') {
    return header
  }

  return undefined
}

export function DataTable<Data extends object>({
  data = [],
  columns,
  loading,
  className,
  withSortableColumns = false,
  resetTableListeningState = false,
  isFilterOn,
  applyFilters,
  borderRadius,
  bg,
  dividerColor,
  borderWidth = 0,
  borderColor,
  removeLastLine = false,
  noDataText = 'No Data',
  headerBorderBottom,
}: IProps<Data>) {
  const t = useTranslations()
  const defaultBg = useColorModeValue('white', '#0F0F0F')
  const resolvedBg = bg ?? defaultBg
  const footerBg = useColorModeValue('gray.50', '#1A1A1A')
  const headerColor = useColorModeValue('gray.600', 'white')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    manualPagination: true,
  })

  /* sometimes, we will require to reset the table to default features on some circunstances, bring an state in order to reset table */
  useEffect(() => {
    table.reset()
  }, [resetTableListeningState])

  return (
    <TableContainer
      w="full"
      borderRadius={borderRadius}
      borderWidth={borderWidth}
      borderColor={borderColor}
    >
      <Table pos="relative" variant="simple" className={className}>
        <Thead bg={resolvedBg}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr
              key={headerGroup.id}
              cursor={withSortableColumns ? 'pointer' : 'unset'}
            >
              {headerGroup.headers.map((header) => {
                return (
                  <Th
                    key={header.id}
                    onClick={
                      withSortableColumns
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    minW={'37px'}
                    textTransform={'none'}
                    fontSize={'xs'}
                    fontWeight={'600'}
                    zIndex={0}
                    textAlign={
                      header.id === 'minerModelName' ? 'left' : 'center'
                    }
                    color={headerColor}
                    borderBottomColor={dividerColor}
                    borderBottom={headerBorderBottom}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {withSortableColumns && (
                      <chakra.span pl="2">
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === 'desc' ? (
                            <TriangleDownIcon
                              color={'brand.600'}
                              aria-label="sorted descending"
                            />
                          ) : (
                            <TriangleUpIcon
                              color={'brand.600'}
                              aria-label="sorted ascending"
                            />
                          )
                        ) : null}
                      </chakra.span>
                    )}
                  </Th>
                )
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody bg={resolvedBg}>
          {table.getRowModel().rows?.length > 0 &&
            table.getRowModel().rows.map((row) => {
              return (
                <Tr
                  key={row.id}
                  _notLast={{ borderBottomColor: dividerColor }}
                  _last={removeLastLine ? { borderBottom: 'none' } : undefined}
                >
                  {row.getVisibleCells().map((cell: any) => (
                    <Td
                      data-column={getDataColumnValue(cell)}
                      pb="8px"
                      key={cell.id}
                      h={{ base: 'auto', md: 50 }}
                      className={
                        cell.column.columnDef.meta?.additionalTdClass || ''
                      }
                      borderBottomColor={dividerColor}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              )
            })}
        </Tbody>
        {table
          .getFooterGroups()
          .some((footerGroup) =>
            footerGroup.headers.some((header) => header.column.columnDef.footer)
          ) && (
          <Tfoot bg={footerBg}>
            {table.getFooterGroups().map((footerGroup) => (
              <Tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <Td
                    key={header.id}
                    py="8px"
                    h={{ base: 'auto', md: 50 }}
                    fontWeight="bold"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.footer,
                          header.getContext()
                        )}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tfoot>
        )}
      </Table>
      {loading && <Progress isIndeterminate colorScheme="brand" size="xs" />}
      {!isFilterOn && data && data.length === 0 && !loading && (
        <Text textAlign="center" fontWeight="bold" fontSize="12px" py={5}>
          {noDataText}
        </Text>
      )}
      {isFilterOn && data && data.length === 0 && !loading && (
        <Box w="full" p={3} rounded="lg" mb="1">
          <Center flexDirection="column">
            <Text textAlign="center" fontWeight="bold" fontSize="12px" py={5}>
              {t('dashboard.billing.no-filter-data-credits')}
            </Text>
            <Button
              colorScheme="brand"
              size="sm"
              variant={'ghost'}
              onClick={() => {
                applyFilters && applyFilters(null)
              }}
            >
              <Text decoration={'underline'} fontWeight={'400'}>
                {t('dashboard.billing.clear-filters')}
              </Text>
            </Button>
          </Center>
        </Box>
      )}
    </TableContainer>
  )
}
